import React, { useState } from 'react';
import Box from '@amzn/meridian/box';
import Icon from '@amzn/meridian/icon';
import Tag from '@amzn/meridian/tag';
import Text from '@amzn/meridian/text';
import Toggle from '@amzn/meridian/toggle';
import chevronRightSmallTokens from '@amzn/meridian-tokens/base/icon/chevron-right-small';
import chevronDownSmallTokens from '@amzn/meridian-tokens/base/icon/chevron-down-small';
import { ProcessPath, ProcessPathMetrics, StaffingMode, PathType, Workstation, AssignmentAssociate, AssignModalTarget } from '../models';
import WorkstationCard from './WorkstationCard';
import IndirectAssociateRow from './IndirectAssociateRow';

export interface ProcessPathSectionProps {
  processPath: ProcessPath;
  expanded: boolean;
  onToggleExpanded: (processPathId: string) => void;
  /**
   * Fires when the user activates the per-row remove control on an
   * assigned associate (within a workstation card for direct paths, or
   * within an indirect-associate row for indirect paths).
   *
   * Optional: not used when `readOnly` is `true`, since the per-row
   * remove control is suppressed in the children in that mode and there
   * is no caller for it.
   */
  onAssociateRemove?: (associateId: string, locationId: string) => void;
  /**
   * Fires when the user clicks the "Add associate" footer button on a
   * workstation card.
   *
   * Optional: not used when `readOnly` is `true`, since the "Add
   * associate" footer button is suppressed in the children in that
   * mode.
   */
  onAddAssociate?: (target: AssignModalTarget) => void;
  /** Set of currently selected associate IDs (for swap selection) */
  selectedAssociateIds?: Set<string>;
  /** Callback when a checkbox is toggled on an associate row */
  onToggleSelect?: (associateId: string) => void;
  /**
   * When `true`, the section renders in informational mode for the
   * Published Assignment Screen: `readOnly` is forwarded to every child
   * `WorkstationCard` and `IndirectAssociateRow` so their per-row
   * checkboxes, remove controls, and "Add associate" footer button are
   * suppressed. The collapse/expand toggle and the "Show workstations"
   * toggle remain functional — they are display-only controls and do
   * not mutate plan data. The badge ("Direct" / "Indirect"), per-path
   * metrics row, and the workstation grid / indirect-associate row
   * strip are unchanged.
   *
   * Defaults to `false`, in which case behavior is bit-for-bit
   * identical to the editable Assignments page.
   */
  readOnly?: boolean;
}

/**
 * A single metric cell rendered inside the per-path metrics row.
 */
const MetricCell: React.FC<{ label: string; value: number | string }> = ({ label, value }) => (
  <Box className="assignments-process-path__metric" spacingInset="200">
    <Text type="b200">{label}</Text>
    <Text type="h100">{String(value)}</Text>
  </Box>
);

/**
 * Render the metrics row for PROCESS_LINES staffing mode.
 * Surfaces flow-oriented numbers (total flow, delta).
 */
const ProcessLinesMetricsRow: React.FC<{ metrics: ProcessPathMetrics }> = ({ metrics }) => (
  <Box
    className="assignments-process-path__metrics assignments-process-path__metrics--process-lines"
    spacingInset="200"
  >
    <MetricCell label="Total flow" value={metrics.totalFlow ?? '—'} />
    <MetricCell label="Actual" value={metrics.actual} />
    <MetricCell label="Assigned" value={metrics.assigned} />
    <MetricCell label="Delta" value={metrics.delta} />
  </Box>
);

/**
 * Render the metrics row for DEPARTMENT staffing mode.
 * Surfaces processed packages, scan rate, etc.
 */
const DepartmentMetricsRow: React.FC<{ metrics: ProcessPathMetrics }> = ({ metrics }) => (
  <Box
    className="assignments-process-path__metrics assignments-process-path__metrics--department"
    spacingInset="200"
  >
    <MetricCell label="Actual" value={metrics.actual} />
    <MetricCell label="Assigned" value={metrics.assigned} />
    <MetricCell label="Delta" value={metrics.delta} />
    <MetricCell label="DR" value={metrics.dr ?? '—'} />
    <MetricCell label="Avg scan rate" value={metrics.avgScanRate ?? '—'} />
    <MetricCell label="Units per hour" value={metrics.unitsPerHour ?? '—'} />
    <MetricCell label="Processed packages" value={metrics.processedPackages ?? '—'} />
  </Box>
);

/**
 * Collapsible section representing a single process path.
 *
 * Rendering is driven by `staffingMode`:
 *   - PROCESS_LINES: workstation cards (lines with capacity/slots)
 *   - DEPARTMENT: associate pool from workstations[0].assignedAssociates
 *
 * When expanded:
 *   - Direct paths render a per-path metrics row. Workstation cards are
 *     rendered only when "Show workstations" is on.
 *   - Indirect paths render a per-path metrics row. The horizontal flex
 *     row of `IndirectAssociateRow` items is rendered only when
 *     "Show workstations" is on.
 *
 * The component is a thin renderer: all associate / workstation state is
 * passed in via the `processPath` prop, and mutations are reported up to
 * the parent via `onAssociateRemove` and `onAddAssociate`.
 *
 * When `readOnly` is `true` (Published Assignment Screen): `readOnly` is
 * forwarded to every child `WorkstationCard` and `IndirectAssociateRow`
 * so their per-row checkboxes, remove controls, and "Add associate"
 * footer button are suppressed. The collapse/expand toggle and the
 * "Show workstations" toggle remain functional — they are display-only
 * controls and do not mutate plan data.
 */
const ProcessPathSection: React.FC<ProcessPathSectionProps> = ({
  processPath,
  expanded,
  onToggleExpanded,
  onAssociateRemove,
  onAddAssociate,
  selectedAssociateIds,
  onToggleSelect,
  readOnly = false,
}) => {
  const isProcessLines = processPath.staffingMode === StaffingMode.PROCESS_LINES;
  const isDirect = processPath.pathType === PathType.DIRECT;
  const badgeLabel = isDirect ? 'Direct' : 'Indirect';

  // Workstations / associates are visible by default. The toggle stays
  // local to each section so toggling one path doesn't affect another.
  const [workstationsVisible, setWorkstationsVisible] = useState<boolean>(true);

  const handleHeaderClick = () => {
    onToggleExpanded(processPath.fclmFunctionId);
  };

  const handleWorkstationsToggle = (nextChecked: boolean) => {
    setWorkstationsVisible(nextChecked);
  };

  const chevronTokens = expanded ? chevronDownSmallTokens : chevronRightSmallTokens;

  // For DEPARTMENT mode, associates come from the explicit
  // `indirectAssociates` list when present, otherwise they fall back to
  // workstations[0].assignedAssociates.
  const departmentAssociates: AssignmentAssociate[] =
    processPath.indirectAssociates && processPath.indirectAssociates.length > 0
      ? processPath.indirectAssociates
      : !isProcessLines && processPath.workstations.length > 0
        ? processPath.workstations[0].assignedAssociates
        : [];

  return (
    <Box className="assignments-process-path" spacingInset="300">
      <Box className="assignments-process-path__header-row">
        {/*
          Native <button> gives us click + keyboard activation without
          depending on Meridian Box exposing onClick in its typings. The
          "Show workstations" Toggle is rendered as a sibling (not a
          child) so it doesn't end up nested inside another button — and
          so its own click/keyboard handling drives the toggle directly
          rather than collapsing the section.
        */}
        <button
          type="button"
          className="assignments-process-path__header"
          onClick={handleHeaderClick}
          aria-expanded={expanded}
        >
          <Icon tokens={chevronTokens} />
          <Text type="h200">{processPath.processPathName}</Text>
          <Tag type={isDirect ? 'neutral' : 'theme'}>{badgeLabel}</Tag>
        </button>
        <Box className="assignments-process-path__header-actions">
          {/*
            Wrapping div carries the test id so the existing tests that
            query the toggle by its label/role keep working, and
            additional integrations can target the wrapper without
            depending on Meridian internals.
          */}
          <Box
            className="assignments-process-path__show-workstations-toggle"
            data-testid={`process-path-show-workstations-${processPath.fclmFunctionId}`}
          >
            <Toggle checked={workstationsVisible} onChange={handleWorkstationsToggle} size="small">
              Show workstations
            </Toggle>
          </Box>
        </Box>
      </Box>

      {expanded && (
        <Box className="assignments-process-path__body">
          {isProcessLines ? (
            <ProcessLinesMetricsRow metrics={processPath.metrics} />
          ) : (
            <DepartmentMetricsRow metrics={processPath.metrics} />
          )}

          {workstationsVisible &&
            (isProcessLines ? (
              <Box className="assignments-process-path__workstations">
                {processPath.workstations.map((workstation: Workstation) => (
                  <WorkstationCard
                    key={workstation.workstationId}
                    workstation={workstation}
                    onAssociateRemove={onAssociateRemove}
                    onAddAssociate={(wsId) => onAddAssociate?.({
                      workstationId: wsId,
                      workstationName: workstation.workstationName,
                      fclmFunctionId: processPath.fclmFunctionId,
                      fclmProcessPathId: processPath.fclmProcessPathId ?? null,
                    })}
                    selectedAssociateIds={selectedAssociateIds}
                    onToggleSelect={onToggleSelect}
                    readOnly={readOnly}
                  />
                ))}
              </Box>
            ) : (
              <Box className="assignments-process-path__indirect-associates">
                {departmentAssociates.map((associate) => (
                  <IndirectAssociateRow
                    key={associate.associateId}
                    associate={associate}
                    processPathId={processPath.fclmFunctionId}
                    onRemove={onAssociateRemove}
                    readOnly={readOnly}
                  />
                ))}
              </Box>
            ))}
        </Box>
      )}
    </Box>
  );
};

export default ProcessPathSection;
